// src/components/modals/SendSignaturitModal.jsx
import { useEffect, useState, useMemo } from "react";
import { Modal, Button, Form } from "react-bootstrap";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SendSignaturitModal({
    show,
    onHide,
    onClose, // Optional callback after success
    close,
    handleClose,
    defaultEmail = "",
    onConfirm, // (email, signatureType) => Promise<void> | void
    signatureType = "email",
}) {
    const [mode, setMode] = useState(defaultEmail ? "default" : "custom");
    const [email, setEmail] = useState(defaultEmail || "");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    const effectiveEmail = useMemo(() => {
        return mode === "default" ? defaultEmail : email.trim();
    }, [mode, defaultEmail, email]);

    const canSend = useMemo(() => {
        return emailRegex.test(effectiveEmail);
    }, [effectiveEmail]);

    useEffect(() => {
        if (!show) return;
        const normalizedDefaultEmail = defaultEmail || "";
        setMode(normalizedDefaultEmail ? "default" : "custom");
        setEmail(normalizedDefaultEmail);
        setError("");
    }, [defaultEmail, show]);

    const closeModal = () => {
        if (onHide) {
            onHide();
            return;
        }
        if (typeof close === "function") {
            close(false);
            return;
        }
        if (typeof handleClose === "function") {
            handleClose(false);
        }
    };

    const handleConfirm = async () => {
        setError("");
        if (!canSend) {
            setError("Introduce un email válido.");
            return;
        }
        try {
            setSending(true);
            await onConfirm(effectiveEmail, signatureType);
            if (onClose) onClose(); // Notification callback
            closeModal();
        } catch (e) {
            setError(e?.message || "No se pudo enviar a Signaturit.");
        } finally {
            setSending(false);
        }
    };

    return (
        <Modal show={show} onHide={closeModal} centered contentClassName="border-0 rounded-4 shadow-lg">
            <Modal.Header className="bg-white border-bottom-0" closeButton>
                <Modal.Title className="fw-bold text-primary">Enviar a firmar con Signaturit</Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-0">
                <Form>
                    <Form.Label className="mb-3 fw-bold text-muted small text-uppercase">
                        Destinatario
                    </Form.Label>

                    <div className="d-flex flex-column gap-3 p-3 bg-light rounded-3 border border-light">
                        <Form.Check
                            type="radio"
                            id="signaturit-mode-default"
                            name="signaturit-mode"
                            label={
                                <span className={mode === "default" ? "fw-bold text-dark" : "text-muted"}>
                                    Usar dirección existente{" "}
                                    {defaultEmail ? (
                                        <span className="text-primary ms-1">({defaultEmail}) (Titular)</span>
                                    ) : (
                                        <span className="text-muted small ms-1">(no disponible)</span>
                                    )}
                                </span>
                            }
                            disabled={!defaultEmail}
                            checked={mode === "default"}
                            onChange={() => setMode("default")}
                            className="mb-1"
                        />

                        <div>
                            <Form.Check
                                type="radio"
                                id="signaturit-mode-custom"
                                name="signaturit-mode"
                                label={<span className={mode === "custom" ? "fw-bold text-dark" : "text-muted"}>Usar otra dirección</span>}
                                checked={mode === "custom"}
                                onChange={() => setMode("custom")}
                                className="mb-2"
                            />

                            {mode === "custom" && (
                                <div className="ms-4 mt-2">
                                    <Form.Control
                                        type="email"
                                        placeholder="nombre@dominio.com"
                                        value={mode === "custom" ? email : effectiveEmail}
                                        onChange={(e) => setEmail(e.target.value)}
                                        isInvalid={!!error}
                                        className="rounded-3 shadow-sm border-0"
                                        autoFocus
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {error}
                                    </Form.Control.Feedback>
                                </div>
                            )}
                        </div>
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer className="border-top-0 pt-2 pb-4">
                <Button variant="outline-secondary" className="rounded-pill px-4" onClick={closeModal} disabled={sending}>
                    Cancelar
                </Button>
                <Button variant="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={handleConfirm} disabled={sending || !canSend}>
                    {sending ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Enviando...
                        </>
                    ) : "Enviar Documento"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
